import React, {useRef, useMemo, ReactNode, useState, useEffect} from 'react';
import {ReactComponent as DateIcon} from '@assets/icons/invitation/date-icon.svg';
import {ReactComponent as FilterIcon} from '@/assets/icons/filter.svg';
import DownArrow from './down-arrow';
import Picker from '@/components/business/filter/picker';
import {formatDate, cn} from '@/utils';
import {useFilterModal} from '@/components/basic/filter-modal';
import {useTranslation} from 'react-i18next';

export interface DateFilterProps {
  startDate: number;
  endDate: number;
  onDateConfirm: (range: {startDate: number; endDate: number}) => void;
}

export const DateFilter: React.FC<DateFilterProps> = ({
  startDate,
  endDate,
  onDateConfirm,
}) => {
  const pickerRef = useRef<any>(null);

  const dateStr = useMemo(() => {
    if (!startDate || !endDate) {
      return '';
    }
    // 比较日期部分而不是完整时间戳
    const startDateStr = formatDate(startDate, 'dd/MM');
    const endDateStr = formatDate(endDate, 'dd/MM');
    if (startDateStr === endDateStr) {
      return startDateStr;
    }
    return `${startDateStr}-${endDateStr}`;
  }, [startDate, endDate]);

  const handleOpenDatePicker = () => {
    pickerRef.current?.handleOpen();
  };

  return (
    <>
      <div
        onClick={handleOpenDatePicker}
        className="gap-x-1 min-w-0 px-2 h-7 flex flex-row items-center justify-center rounded border border-gray-300 bg-white">
        <DateIcon className="w-4 h-4 text-t2" />
        <div className="min-w-10">{dateStr}</div>
        <DownArrow className="size-2 text-t4" />
      </div>
      <div className="absolute">
        <Picker
          ref={pickerRef}
          handleConfirm={onDateConfirm}
          validRange={{
            startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000), // 60天前
            endDate: new Date(), // 今天
          }}
        />
      </div>
    </>
  );
};

export type SortType = 'asc' | 'desc' | 'none';

export interface SortFilterProps {
  className?: string;
  textClassName?: string;
  sort: SortType;
  children?: ReactNode;
  requireSort?: boolean;
  onChange?: (sort: SortType) => void;
}

const COLOR = {
  active: 'text-c1',
  inactive: 'text-t2',
};

export const SortFilter: React.FC<SortFilterProps> = ({
  children,
  sort,
  className,
  textClassName,
  requireSort,
  onChange,
}) => {
  const sortData = useMemo(() => {
    const sortMap: Record<
      SortType,
      {up: string; down: string; next: SortType}
    > = {
      asc: {
        up: COLOR.active,
        down: COLOR.inactive,
        next: 'desc',
      },
      desc: {
        up: COLOR.inactive,
        down: COLOR.active,
        next: requireSort ? 'asc' : 'none',
      },
      none: {
        up: COLOR.inactive,
        down: COLOR.inactive,
        next: 'asc',
      },
    };
    return sortMap[sort];
  }, [sort, requireSort]);

  return (
    <div
      className={`flex flex-row items-center gap-x-1 ${className}`}
      onClick={() => onChange?.(sortData.next)}>
      <span className={textClassName}>{children}</span>
      <div>
        <DownArrow className={cn('size-2 rotate-180', sortData.up)} />
        <DownArrow className={cn('size-2', sortData.down)} />
      </div>
    </div>
  );
};

export interface TeamFilterProps {
  dateRange?: [number, number];
  onChangeDateRange?: (startDate?: number, endDate?: number) => void;
}

export const TeamFilter: React.FC<TeamFilterProps> = ({
  dateRange: _dateRange,
  onChangeDateRange,
}) => {
  const {t} = useTranslation();
  const [filterValue, setFilterValue] = useState('');

  const filterModalOptions = useMemo(() => {
    return [
      {
        value: '0',
        title: t('filter.today'),
      },
      {
        value: '1',
        title: t('filter.yesterday'),
      },
      {
        value: '7',
        title: t('filter.days', {day: 7}),
      },
      {
        value: '15',
        title: t('filter.days', {day: 15}),
      },
      {
        value: '30',
        title: t('filter.days', {day: 30}),
      },
    ];
  }, [t]);

  const handleFilterValue = (value: string) => {
    setFilterValue(value);
    if (value === '') {
      return;
    }
    const perDayMs = 1000 * 60 * 60 * 24;
    if (value === '0') {
      onChangeDateRange?.(Date.now(), Date.now());
      return;
    }
    // 修改：昨天是特殊情况，其他天数应该到今天
    if (value === '1') {
      onChangeDateRange?.(Date.now() - perDayMs, Date.now() - perDayMs);
    } else {
      onChangeDateRange?.(Date.now() - perDayMs * +value, Date.now());
    }
  };

  const {renderModal, show, currentValue} = useFilterModal(
    t('filter.time'),
    filterModalOptions,
    false,
  );

  useEffect(() => {
    if (currentValue != null) {
      handleFilterValue(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  return (
    <>
      <div
        onClick={() => show(filterValue)}
        className="px-2 py-0 min-w-0 h-7 flex flex-row items-center justify-center rounded border border-gray-300 bg-white gap-x-1">
        <FilterIcon className="w-3.5 h-3.5 text-t2" />
        <span className="text-gray-600 text-xs">{t('label.filter')}</span>
        <DownArrow className="size-2 text-t4" />
      </div>
      {renderModal}
    </>
  );
};
